/**
 * Triangular grid renderer
 * Requirements: 6.2, 6.4
 */

import type { Position, SimulationState, SimulationConfig } from '@langton/shared';
import { BaseRenderer } from './base-renderer';

/**
 * Renderer for triangular grid cells
 * Draws cells as alternating up and down triangles
 * 
 * Triangle geometry:
 * - Each triangle has width = cellSize and height = cellSize
 * - Triangles are arranged with horizontal spacing = cellSize / 2
 * - Up triangles: apex at top, base at bottom
 * - Down triangles: apex at bottom, base at top
 */
export class TriangularRenderer extends BaseRenderer {
  /**
   * Triangular grid has 3 directions
   */
  getDirectionCount(): number {
    return 3;
  }

  /**
   * Use transparent background for proper tile overlap
   */
  protected usesTransparentBackground(): boolean {
    return true;
  }

  /**
   * Calculate actual pixel dimensions for triangular grid
   * The grid width in pixels = (gridWidth / 2) * cellSize + cellSize/2
   * because each pair of triangles (up+down) takes cellSize width
   */
  getOffscreenDimensions(gridWidth: number, gridHeight: number, cellSize: number): { width: number; height: number } {
    // Each triangle takes halfWidth horizontal space
    // Total width = number of triangles * halfWidth + extra halfWidth for last triangle
    const halfWidth = cellSize / 2;
    const width = gridWidth * halfWidth + halfWidth;
    const height = gridHeight * cellSize;
    
    return { width, height };
  }

  /**
   * Get angle for direction for visual rendering.
   * 
   * Direction in triangular grid points to the EDGE the ant will move through.
   * This is the result after turn() is called.
   * 
   * For up-pointing triangle:
   * - Edge 0 (left): points to upper-left (-120° = -2π/3)
   * - Edge 1 (right): points to upper-right (-60° = -π/3)
   * - Edge 2 (bottom): points down (180° = π)
   */
  getDirectionAngle(direction: number): number {
    // Angles for up-pointing triangle edges (default)
    // The avatar image points "up" (negative Y), so we rotate to face the edge
    const edgeAngles = [
      Math.PI / 6,        // 0: left edge - face upper-left (30° from up, towards left)
      -Math.PI / 6,       // 1: right edge - face upper-right (-30° from up, towards right)  
      Math.PI             // 2: bottom edge - face down (180°)
    ];
    return edgeAngles[direction % 3];
  }

  /**
   * Convert internal direction (vertex) to visual direction (edge).
   * 
   * After turn(), direction points to the EDGE the ant will move through.
   * No conversion needed - just return the direction.
   */
  private vertexToEdge(direction: number, _isUp: boolean): number {
    return direction % 3;
  }

  /**
   * Get angle for direction considering triangle orientation.
   * This is used for avatar rotation.
   * 
   * Direction points to the EDGE the ant will move through.
   * We need to rotate the avatar to face that edge.
   */
  getDirectionAngleForCell(direction: number, x: number, y: number): number {
    const isUp = this.isUpTriangle(x, y);
    const edge = direction % 3;
    
    if (isUp) {
      // Up-pointing triangle:
      // Edge 0 (left): goes to upper-left neighbor
      // Edge 1 (right): goes to upper-right neighbor  
      // Edge 2 (bottom): goes to bottom neighbor
      const upAngles = [
        Math.PI / 6,        // 0: left edge - face upper-left (30°)
        -Math.PI / 6,       // 1: right edge - face upper-right (-30°)
        Math.PI             // 2: bottom edge - face down (180°)
      ];
      return upAngles[edge];
    } else {
      // Down-pointing triangle:
      // Edge 0 (left): goes to lower-left neighbor
      // Edge 1 (right): goes to lower-right neighbor
      // Edge 2 (top): goes to top neighbor
      const downAngles = [
        5 * Math.PI / 6,    // 0: left edge - face lower-left (150°)
        7 * Math.PI / 6,    // 1: right edge - face lower-right (210° = -150°)
        0                   // 2: top edge - face up (0°)
      ];
      return downAngles[edge];
    }
  }

  /**
   * Determine if a triangle at (x, y) points up or down
   * Alternating pattern: (x + y) % 2 === 0 -> up
   */
  isUpTriangle(x: number, y: number): boolean {
    return (x + y) % 2 === 0;
  }

  /**
   * Calculate cell position in canvas coordinates
   * Triangles are arranged in a tessellating pattern with half-width spacing
   */
  getCellPosition(x: number, y: number, cellSize: number): Position {
    const halfWidth = cellSize / 2;
    return {
      x: x * halfWidth,
      y: y * cellSize
    };
  }

  /**
   * Draw a triangular cell
   * Clears the area first to prevent antialiasing artifacts from previous colors
   * Requirements: 6.2, 6.4
   */
  drawCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    cellSize: number
  ): void {
    const halfWidth = cellSize / 2;
    const isUp = this.isUpTriangle(x, y);
    
    // Calculate base position
    const baseX = x * halfWidth;
    const baseY = y * cellSize;

    // Create triangle path
    const createTrianglePath = (): void => {
      ctx.beginPath();
      if (isUp) {
        // Up-pointing triangle
        ctx.moveTo(baseX + halfWidth, baseY);              // Top center
        ctx.lineTo(baseX + cellSize, baseY + cellSize);    // Bottom right
        ctx.lineTo(baseX, baseY + cellSize);               // Bottom left
      } else {
        // Down-pointing triangle
        ctx.moveTo(baseX, baseY);                          // Top left
        ctx.lineTo(baseX + cellSize, baseY);               // Top right
        ctx.lineTo(baseX + halfWidth, baseY + cellSize);   // Bottom center
      }
      ctx.closePath();
    };

    // Create slightly larger triangle for clearing antialiasing artifacts
    const createLargerTrianglePath = (): void => {
      const expand = 1;
      ctx.beginPath();
      if (isUp) {
        ctx.moveTo(baseX + halfWidth, baseY - expand);
        ctx.lineTo(baseX + cellSize + expand, baseY + cellSize + expand);
        ctx.lineTo(baseX - expand, baseY + cellSize + expand);
      } else {
        ctx.moveTo(baseX - expand, baseY - expand);
        ctx.lineTo(baseX + cellSize + expand, baseY - expand);
        ctx.lineTo(baseX + halfWidth, baseY + cellSize + expand);
      }
      ctx.closePath();
    };

    // Clear the triangle area first to remove any antialiasing artifacts
    ctx.save();
    createLargerTrianglePath();
    ctx.clip();
    ctx.clearRect(baseX - 2, baseY - 2, cellSize + 4, cellSize + 4);
    ctx.restore();

    // Draw the triangle with new color
    ctx.fillStyle = color;
    createTrianglePath();
    ctx.fill();
  }

  /**
   * Get triangular clipping path for avatar
   * Requirements: 5.6
   */
  getAvatarClipPath(
    _ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    size: number,
    isUp: boolean = true
  ): Path2D {
    const path = new Path2D();
    const halfSize = size / 2;
    const triangleHeight = (size * Math.sqrt(3)) / 2;

    if (isUp) {
      // Up-pointing triangle: apex at top
      path.moveTo(centerX, centerY - triangleHeight / 2);
      path.lineTo(centerX + halfSize, centerY + triangleHeight / 2);
      path.lineTo(centerX - halfSize, centerY + triangleHeight / 2);
    } else {
      // Down-pointing triangle: apex at bottom
      path.moveTo(centerX, centerY + triangleHeight / 2);
      path.lineTo(centerX + halfSize, centerY - triangleHeight / 2);
      path.lineTo(centerX - halfSize, centerY - triangleHeight / 2);
    }
    path.closePath();

    return path;
  }

  /**
   * Override drawAntInTile to handle triangle orientation
   */
  protected drawAntInTile(
    ctx: CanvasRenderingContext2D,
    state: SimulationState,
    config: SimulationConfig,
    tileOffsetX: number,
    tileOffsetY: number,
    cellSize: number,
    transform: { mirrorX: boolean; mirrorY: boolean }
  ): void {
    const { width, height } = config;

    // Calculate ant position in this tile
    let antX = state.ant.position.x;
    let antY = state.ant.position.y;

    if (transform.mirrorX) antX = width - 1 - antX;
    if (transform.mirrorY) antY = height - 1 - antY;

    const cellPos = this.getCellPosition(antX, antY, cellSize);
    const screenX = tileOffsetX + cellPos.x + cellSize / 2;
    const screenY = tileOffsetY + cellPos.y + cellSize / 2;

    // Check if ant is visible
    if (screenX < -cellSize || screenX > ctx.canvas.width + cellSize) return;
    if (screenY < -cellSize || screenY > ctx.canvas.height + cellSize) return;

    // Determine triangle orientation at ant position
    const isUp = this.isUpTriangle(antX, antY);

    // Calculate rotation angle based on triangle orientation
    let angle = this.getDirectionAngleForCell(state.ant.direction, antX, antY);

    // Apply mirroring to angle
    if (transform.mirrorX) angle = Math.PI - angle;
    if (transform.mirrorY) angle = -angle;

    const avatarSize = cellSize * 0.8;

    ctx.save();
    ctx.translate(screenX, screenY);

    // Apply clipping with correct triangle orientation
    const clipPath = this.getAvatarClipPath(ctx, 0, 0, avatarSize, isUp);
    if (clipPath) {
      ctx.clip(clipPath);
    }

    // Now rotate only the image inside the mask
    ctx.rotate(angle);

    // Draw avatar image if loaded
    if (this.avatarImage) {
      ctx.drawImage(this.avatarImage, -avatarSize / 2, -avatarSize / 2, avatarSize, avatarSize);
    } else {
      // Fallback: red circle with direction indicator
      ctx.fillStyle = '#FF0000';
      ctx.beginPath();
      ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
      ctx.fill();

      // Direction indicator
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -avatarSize / 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Override renderPreview to handle triangle orientation
   */
  renderPreview(state: SimulationState, config: SimulationConfig, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate cells to show based on canvas aspect ratio (3:2 for preview)
    const aspectRatio = canvas.width / canvas.height;
    const baseCells = 15;
    
    let previewCellsX: number;
    let previewCellsY: number;
    
    if (aspectRatio >= 1) {
      previewCellsY = baseCells;
      previewCellsX = Math.ceil(baseCells * aspectRatio);
    } else {
      previewCellsX = baseCells;
      previewCellsY = Math.ceil(baseCells / aspectRatio);
    }
    
    const cellSize = Math.min(canvas.width / previewCellsX, canvas.height / previewCellsY);
    
    // Clear with background color
    ctx.fillStyle = config.colors[0] || '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate offset to center ant on screen
    const antX = state.ant.position.x;
    const antY = state.ant.position.y;
    
    // Get ant's pixel position using grid-specific method
    const antCellPos = this.getCellPosition(antX, antY, cellSize);
    
    // Offset to center ant on canvas
    const offsetX = canvas.width / 2 - antCellPos.x - cellSize / 2;
    const offsetY = canvas.height / 2 - antCellPos.y - cellSize / 2;
    
    // Calculate visible cell range (in virtual coordinates around ant)
    const margin = 3;
    const halfCellsY = Math.ceil(previewCellsY / 2) + margin;
    const halfCellsX = Math.ceil(previewCellsX) + margin * 2;
    
    // Draw visible cells - iterate around ant position
    ctx.save();
    ctx.translate(offsetX, offsetY);
    
    for (let dy = -halfCellsY; dy <= halfCellsY; dy++) {
      for (let dx = -halfCellsX; dx <= halfCellsX; dx++) {
        // Virtual position (for drawing)
        const vx = antX + dx;
        const vy = antY + dy;
        
        // Wrapped position (for getting color from grid)
        const gx = ((vx % config.width) + config.width) % config.width;
        const gy = ((vy % config.height) + config.height) % config.height;
        
        const colorIndex = state.grid[gy]?.[gx] ?? 0;
        
        // Skip background cells (already filled)
        if (colorIndex === 0) continue;
        
        const color = config.colors[colorIndex] || config.colors[0];
        
        // Draw at virtual position (vx, vy) so geometry is correct
        this.drawCell(ctx, vx, vy, color, cellSize);
      }
    }
    
    ctx.restore();
    
    // Draw ant at center of canvas
    const avatarSize = cellSize * 0.8;
    
    // Determine triangle orientation at ant position
    const isUp = this.isUpTriangle(antX, antY);
    
    // Get angle based on triangle orientation
    const angle = this.getDirectionAngleForCell(state.ant.direction, antX, antY);

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // Apply clipping with correct triangle orientation
    const clipPath = this.getAvatarClipPath(ctx, 0, 0, avatarSize, isUp);
    if (clipPath) {
      ctx.clip(clipPath);
    }
    
    ctx.rotate(angle);

    if (this.avatarImage) {
      ctx.drawImage(this.avatarImage, -avatarSize / 2, -avatarSize / 2, avatarSize, avatarSize);
    } else {
      ctx.fillStyle = '#FF0000';
      ctx.beginPath();
      ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -avatarSize / 2);
      ctx.stroke();
    }

    ctx.restore();
  }
}
